import React, {
  useState, useRef, useMemo,
} from 'react';
import { object, number, array } from 'yup';
import {
  Formik,
  Form,
} from 'formik';
import isEqualWith from 'lodash/isEqualWith';
import uniq from 'lodash/uniq';
import { shuffle } from 'lodash/collection';

import _ from 'lodash';
import useHttpStatus from '~/commons/hooks/useHttpStatus';
import {
  initFormValues,
} from '~/commons/helpers';

import Html from '~/commons/components/Html.jsx';
import ModalAnsConfirm from '../modals/ModalAnsConfirm.jsx';
import FieldsSingleSelect from './FieldsSingleSelect.jsx';
import FieldsMultiSelect from './FieldsMultiSelect.jsx';
import FieldsOrdering from './FieldsOrdering.jsx';
import FieldsMatching from './FieldsMatching.jsx';
import FormAutoSave from './FormAutoSave.jsx';

const formatFields = {
  'single-select': FieldsSingleSelect,
  'multi-select': FieldsMultiSelect,
  ordering: FieldsOrdering,
  matching: FieldsMatching,
};

const schema = object({
  answers: array().of(
    object({
      answered: number().when('format', {
        is: 'single-select',
        then: number().required(),
      }),
      answeredSeq: array()
        .when('format', {
          is: 'multi-select',
          then: array()
            .of(number())
            .min(1)
            .required(),
        })
        .when('format', {
          is: 'ordering',
          then: array()
            .of(number().required())
            .required()
            .test(
              'Duplicates',
              (v) => uniq(v).length === v.length,
            ),
        })
        .when('format', {
          is: 'matching',
          then: array()
            .of(number().required())
            .required()
            .test(
              'Duplicates',
              (v) => uniq(v).length === v.length,
            ),
        }),
    }),
  ),
});

const FormAnswer = ({
  id,
  questions: theQuestions = [],
  answer,
  assignment,
  showAns = false,
  disabled = false,
  startTime,
  getElapsedTime,
  onHide,
  onSubmitted,
  disableShowCorrectAnswers = false,
  autoSave = false,
  disableTotalMarks = false,
  status,
}) => {
  const formVals = useRef();
  const [confirmModal, setConfirmModal] = useState(false);
  const [, req] = useHttpStatus();
  const startTimeRef = useRef(Date.now()); // Track start time for this session

  // Reset start time when component mounts or when starting a new answer session
  React.useEffect(() => {
    if (!answer || (answer && answer.status !== 'completed')) {
      // Reset timer for new answers or answers still in progress
      startTimeRef.current = Date.now();
    }
  }, [id, answer, status]);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Update mobile state on window resize
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const questions = useMemo(() => theQuestions.map((q) => {
    if (!q.randomAnswers) {
      return {
        ...q,
        choices: q.choices.map((c, ii) => ({ c, ii, r: ii })),
        randoms: q.format === 'matching' ? q.choices.map((c, i) => i).concat(q.choices.map((c, i) => i)) : q.choices.map((c, i) => i),
      };
    }
    let { randoms } = answer?.answers?.find((a) => a.question === q._id) || {
      randoms: q.format === 'matching' ? shuffle(q.choices.map((c, i) => i)).concat(shuffle(q.choices.map((c, i) => i))) : shuffle(q.choices.map((c, i) => i)),
    };
    if (q.format === 'matching' && randoms.length < q.choices.length * 2) {
      randoms = randoms.concat(randoms);
    }
    const choices = q.choices.map((c, ii) => ({ c, ii, r: randoms[ii] })).sort((a, b) => a.r - b.r);
    return {
      ...q,
      choices: choices.map((c, jj) => ({ ...c, r: jj })), // Set r to display index
      randoms,
    };
  }), [theQuestions]);
  const defaults = initFormValues({
    answers: questions.map((q) => {
      // Only populate previous answers if answer exists and status is not completed
      // This allows users to continue where they left off for incomplete answers
      const shouldPopulatePreviousAnswers = answer && status !== 'completed';

      const {
        answered,
        answeredSeq,
      } = shouldPopulatePreviousAnswers
        ? (answer?.answers?.find((a) => a.question === q._id) || {})
        : {};

      // Default array for ordering/matching questions
      const defaultArrayForOrderingMatching = ['ordering', 'matching'].includes(q.format)
        ? q.choices.map(() => undefined)
        : [];

      const result = {
        question: q._id,
        format: q.format, // just for validation only
        answered: shouldPopulatePreviousAnswers ? answered : undefined,
        answeredSeq: shouldPopulatePreviousAnswers
          ? (answeredSeq || defaultArrayForOrderingMatching)
          : defaultArrayForOrderingMatching,
        randoms: q.randoms,
      };

      return result;
    }),
  });

  // Calculate time spent in seconds
  const getTimeSpent = () => Math.floor((Date.now() - startTimeRef.current) / 1000);

  const submit = async (complete) => {
    const currentTimeSpent = getTimeSpent();
    // Add to existing time spent if this is an update to an existing answer
    const totalTimeSpent = (answer?.timeSpent || 0) + currentTimeSpent;
    let payload;
    if (_.isNil(id)) {
      ({ payload } = await req({
        url: '/api/quiz/answers',
        method: 'POST',
        data: {
          ...formVals.current,
          assignment,
        },
      }));
    } else {
      ({ payload } = await req({
        url: `/api/quiz/answers/${id}`,
        method: 'patch',
        data: {
          ...formVals.current,
          assignment,
          ...(complete ? { status: 'completed', timeSpent: totalTimeSpent } : {}),
        },
      }));
    }

    if (complete) {
      // Notify parent component with the payload
      // Parent will handle refreshing data to get updated status
      onSubmitted(payload);
    }
  };
  return (
    <>
      <ModalAnsConfirm
        show={confirmModal}
        onConfirm={() => {
          setConfirmModal(false);
          submit(true);
        }}
        onReject={() => setConfirmModal(false)}
      />

      <Formik
        validationSchema={schema}
        initialValues={defaults}
        onSubmit={async (values) => {
          if (!disabled) {
            formVals.current = values;
            setConfirmModal(true);
          }
        }}
      >
        {({ values, isValid }) => {
          // Calculate total score if answer is available
          let totalScore = null;
          if (answer && typeof answer.score === 'number') {
            totalScore = answer.score;
          } else
          if (answer && answer.answer && typeof answer.answer.score === 'number') {
            totalScore = answer.answer.score;
          }
          return (
          <>
          {autoSave && (
            <FormAutoSave
              onSave={() => {
                formVals.current = values;
                submit();
              }}
              saveOn={30 * 1000}
              // onSubmit={(v) => {
              //   submit(v, true);
              // }}
              // submitOn={env.audit ? null : duration * 1000 * 60c}
            />
          )}
          <Form>
            {/* Header with timer and result display - Desktop sticky */}
            {!isMobile && (
              <div style={{
                position: 'sticky',
                top: 0,
                left: 0,
                right: 0,
                display: 'flex',
                // justifyContent: answer ? 'center' : 'space-between',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '15px',
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: '#fafafa',
                marginTop: 0,
                zIndex: 10,
              }}>
                {/* Timer and score display */}
                {!disableTotalMarks && totalScore !== null && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: answer ? '30px' : '8px',
                    color: '#8A3E00',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    flexWrap: 'wrap',
                  }}>
                    {/* {startTime && (
                    <div style={{ alignItems: 'center', gap: '8px' }}>
                      <span>{answer ? '答題時間:' : '答題時間:'}</span>
                      <span style={{
                        color: '#8A3E00',
                        fontFamily: 'monospace',
                        fontSize: '20px',
                      }}>
                        {getElapsedTime()}
                      </span>
                    </div>
                    )} */}
                    {!disableTotalMarks && totalScore !== null && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>總分:</span>
                        <span style={{
                          color: '#8A3E00',
                          fontFamily: 'monospace',
                          fontSize: '20px',
                          fontWeight: 'bold',
                        }}>
                          {totalScore} 分
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Submit button - Desktop only */}
                {status !== 'completed' && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                  }}>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={!isValid}
                      style={{
                        backgroundColor: !isValid ? '#ccc' : '#DE6400',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        cursor: !isValid ? 'not-allowed' : 'pointer',
                      }}
                    >
                      提交答案
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Mobile Header and Submit Button - Sticky at top */}
            {isMobile && (
              <div style={{
                position: 'sticky',
                top: 0,
                left: 0,
                right: 0,
                backgroundColor: '#fff',
                borderBottom: '1px solid #e0e0e0',
                zIndex: 10,
              }}>
                {/* Timer Section */}
                {!disableTotalMarks && totalScore !== null && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '15px',
                    backgroundColor: '#fafafa',
                    gap: 8,
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#8A3E00',
                      fontSize: '16px',
                      fontWeight: 'bold',
                    }}>
                      {/* <span>{answer ? '答題時間:' : '答題時間:'}</span>
                      <span style={{
                        color: '#8A3E00',
                        fontFamily: 'monospace',
                        fontSize: '18px',
                      }}>
                        {getElapsedTime()}
                      </span> */}
                      <span>總分:</span>
                      <span style={{
                        color: '#8A3E00',
                        fontFamily: 'monospace',
                        fontSize: '18px',
                        fontWeight: 'bold',
                      }}>
                        {totalScore} 分
                      </span>
                    </div>
                  </div>
                )}

                {/* Submit Button Section */}
                {status !== 'completed' && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#fff',
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={!isValid}
                        style={{
                          backgroundColor: !isValid ? '#ccc' : '#DE6400',
                          border: 'none',
                          padding: '12px 24px',
                          borderRadius: '25px',
                          color: 'white',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          cursor: !isValid ? 'not-allowed' : 'pointer',
                          width: '100%',
                          maxWidth: '300px',
                        }}
                      >
                        提交答案
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* questions */}
            <div style={{ padding: '0 15px' }}>
              {questions.map((q, i) => {
                const { answered, answeredSeq } = values.answers[i];
                const { format = 'single-select' } = q;
                const FieldAns = formatFields[format];
                let correctAns;
                let isCorrect;

                // get correct answer
                if ((answer || showAns) && !disableShowCorrectAnswers) {
                  correctAns = format === 'single-select'
                    ? q?.answer?.toString()
                    : q?.answers?.map((a) => a.toString());

                  if (format === 'matching') {
                    // Remap correct answers for randomized matches
                    const matchOffset = q.choices.length;
                    const slicedRandoms = q.randoms.slice(matchOffset);
                    const originalCorrect = correctAns
                      .slice(0, slicedRandoms.length)
                      .map((a) => parseInt(a, 10));
                    correctAns = originalCorrect.map((a) => a.toString());
                  }

                  if (format === 'single-select') {
                    isCorrect = answered === correctAns;
                  } else if (format === 'multi-select') {
                    isCorrect = isEqualWith(
                      answeredSeq.sort(),
                      correctAns.sort(),
                      (a, b) => a.toString() === b.toString(),
                    );
                  } else {
                    isCorrect = isEqualWith(
                      answeredSeq,
                      correctAns,
                      (a, b) => a.toString() === b.toString(),
                    );
                  }

                  // Additional matching logic can be added here if needed
                }

                return (
                  <div key={q._id} className="my-4" style={{
                    // Fix for older iOS Safari spacing issues
                    WebkitTransform: 'translateZ(0)',
                    transform: 'translateZ(0)',
                    overflow: 'hidden',
                  }}>
                    <div className="card-body" style={{
                      // Ensure consistent spacing on older iOS
                      padding: '15px',
                      margin: '0',
                      WebkitBoxSizing: 'border-box',
                      boxSizing: 'border-box',
                    }}>
                      {/* question */}
                      <div className="row" style={{
                        margin: '0 0 15px 0',
                        display: 'flex',
                        alignItems: 'flex-start',
                      }}>
                        <h5 className="col-auto pr-0" style={{
                          margin: '0',
                          padding: '0 8px 0 0',
                          flex: 'none',
                        }}>
                          {i + 1}.
                        </h5>
                        <h5 className="col pl-2" style={{
                          margin: '0',
                          padding: '0 0 0 8px',
                          flex: '1',
                        }}>
                          <Html>{q.question}</Html>
                        </h5>
                      </div>
                      {q.img && (
                        <div style={{
                          margin: '15px 0',
                          textAlign: 'center',
                          // Fix iOS Safari image rendering issues
                          WebkitTransform: 'translateZ(0)',
                          transform: 'translateZ(0)',
                          lineHeight: '0', // Prevent extra spacing around image
                          fontSize: '0', // Additional fix for iOS 18 Safari
                        }}>
                          <img
                            className="mw-100"
                            src={q.img}
                            style={{
                              maxWidth: '100%',
                              height: 'auto',
                              display: 'block',
                              margin: '0 auto',
                              // iOS Safari specific fixes
                              WebkitBackfaceVisibility: 'hidden',
                              backfaceVisibility: 'hidden',
                              WebkitTransform: 'translateZ(0)',
                              transform: 'translateZ(0)',
                              verticalAlign: 'top', // Prevent baseline alignment issues
                            }}
                          />
                        </div>
                      )}
                      {/* answer */}
                      <div style={{
                        margin: '15px 0 0 0',
                        // Ensure answer fields don't have extra spacing
                        WebkitTransform: 'translateZ(0)',
                        transform: 'translateZ(0)',
                      }}>
                        <FieldAns
                          qtnIndex={i}
                          question={q}
                          answered={answered}
                          answeredSeq={answeredSeq}
                          isCorrect={isCorrect}
                          correctAns={correctAns}
                          disabled={status === 'completed' || disabled || showAns}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

          </Form>
          </>
          );
        }}
      </Formik>
    </>
  );
};

FormAnswer.displayName = 'FormAnswer';

export default FormAnswer;
